import { useEffect, useState } from "react";
import { checkTokenExpiry } from "../services/authService";

export function useAuth() {
  const [isTokenValid, setIsTokenValid] = useState(true);
  
  // Check if user is logged in as Captain
  const userType = localStorage.getItem("userType");
  const captainToken = localStorage.getItem("captainToken");
  
  // Robust check: user is captain if userType says so OR if captainToken exists
  // This prevents issues where userType might be missing but token persists
  const isCaptain = userType === 'captain' || (!!captainToken && !localStorage.getItem("token"));


  // Determine token and specific auth details based on user type
  const token = isCaptain ? captainToken : localStorage.getItem("token");
  
  // Handle roles
  const storedRole = localStorage.getItem("role")?.toLowerCase();
  const role = isCaptain ? 'captain' : storedRole;
  
  let userRoles = localStorage.getItem("userRoles")
    ? JSON.parse(localStorage.getItem("userRoles")!)
    : [];
    
  // Ensure captain has the captain role in the array
  if (isCaptain) {
    if (!userRoles.includes('captain')) {
       userRoles = ['captain'];
    }
  }

  const plan = localStorage.getItem("plan") || "free trial";
  const planStatus = localStorage.getItem("plan_status");
  const permissions = localStorage.getItem("permissions")
    ? JSON.parse(localStorage.getItem("permissions")!)
    : null;

  // Special override for Basic Plan users - give them full access
  const isBasicPlan = plan.toLowerCase().includes("basic");
  const enhancedPermissions = isBasicPlan
    ? {
        // Override Basic Plan permissions to allow everything
        canManageInventory: true,
        canViewReports: true,
        canManageOnlineOrdering: true,
        canManageTableReservations: true,
        canAddMenuItems: true,
        canAddUsers: true,
        canManageRestaurant: true,

        // Set role-based permissions - if they have a restaurant, they're an owner
        isOwner: role === "owner" || permissions?.isOwner || false,
        isManager: role === "manager" || permissions?.isManager || false,
        isStaff: role === "staff" || permissions?.isStaff || false,
        isAdmin: permissions?.isAdmin || false,
        isSuperAdmin: permissions?.isSuperAdmin || false,

        // Keep subscription status from original
        hasActiveSubscription: permissions?.hasActiveSubscription || true,
        daysRemaining: permissions?.daysRemaining || 30,
        subscriptionStatus: permissions?.subscriptionStatus || "Approved",

        // Use original usage limits but set reasonable values
        maxRestaurants: permissions?.maxRestaurants || 1,
        maxUsers: permissions?.maxUsers || 10,
        maxMenuItems: permissions?.maxMenuItems || 100,
        currentRestaurants: permissions?.currentRestaurants || 0,
        currentUsers: permissions?.currentUsers || 0,
        currentMenuItems: permissions?.currentMenuItems || 0,
      }
    : permissions;

  // Check token validity on hook initialization
  useEffect(() => {
    if (token) {
      let isValid = true;
      
      if (isCaptain) {
         // Manual check for captain token expiry
         try {
             if (captainToken) {
                 const payload = JSON.parse(atob(captainToken.split('.')[1]));
                 if (payload && payload.exp) {
                     isValid = (payload.exp * 1000) > Date.now();
                 }
             } else {
                 isValid = false;
             }
         } catch(e) {
             // If token is not JWT or invalid format, assume valid for now or handle as needed
             // but usually safer to treat as invalid if we expect JWT
             console.error("Error validating captain token", e);
             // Should we invalidate? Maybe not if it's a simple token. 
             // But backend usually sends JWT. Let's assume it's valid if parsing fails to avoid lockout loop 
             // unless we know for sure format is JWT.
             // Given codebase, let's trust it's valid if it exists for now to be safe, 
             // but 'checkTokenExpiry' uses JWT decoding so we should probably try to be consistent.
             isValid = true; 
         }
      } else {
          isValid = checkTokenExpiry();
      }
      
      setIsTokenValid(isValid);

      if (!isValid) {
        // Token is expired, auto logout
        logout();
      }
    }
  }, [token, isCaptain, captainToken]);

  const logout = () => {
    // Clear all authentication-related data
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("role");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("plan");
    localStorage.removeItem("plan_status");
    localStorage.removeItem("hasActiveSubscription");
    localStorage.removeItem("daysRemaining");
    localStorage.removeItem("permissions");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userPhone");
    localStorage.removeItem("userName");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("userFullName");
    localStorage.removeItem("userId");
    localStorage.removeItem("businessId");
    localStorage.removeItem("business_name");
    localStorage.removeItem("restaurant_name");
    localStorage.removeItem("customerToken");
    localStorage.removeItem("customerId");
    
    // Clear Captain Data
    localStorage.removeItem("captainToken");
    localStorage.removeItem("captainUser");
    localStorage.removeItem("userType");

    window.location.href = "/login";
  };

  return {
    isAuthenticated: !!token && isTokenValid,
    role,
    userRoles,
    plan,
    planStatus,
    hasActiveSubscription: enhancedPermissions?.hasActiveSubscription || false,
    daysRemaining: enhancedPermissions?.daysRemaining || 0,
    permissions: enhancedPermissions || permissions, // Use enhanced permissions for Basic Plan, original for others
    token,
    logout,
  };
}
